import DragHandleIcon from "@mui/icons-material/DragHandle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Box, IconButton, Modal } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import Evidence from "../../../../components/Evidence";

const EvidenceModal = ({ show, handleClose, evidence }) => {
  const colors = useColors();
  const [modalSize, setModalSize] = useState({ width: 600, height: 700 });
  const [isResizing, setIsResizing] = useState({ active: false, type: null });
  const initialMousePos = useRef({ x: 0, y: 0 });
  const maxSizeRef = useRef({
    width: window.innerWidth * 0.9,
    height: window.innerHeight * 0.9,
  });

  // Update max size when window resizes
  useEffect(() => {
    const updateMaxSize = () => {
      maxSizeRef.current = {
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.9,
      };
    };

    window.addEventListener("resize", updateMaxSize);
    updateMaxSize(); // Initialize on mount

    return () => window.removeEventListener("resize", updateMaxSize);
  }, []);

  const handleMouseDown = (e, type) => {
    setIsResizing({ active: true, type });
    initialMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isResizing.active) return;

    const deltaX = e.clientX - initialMousePos.current.x;
    const deltaY = e.clientY - initialMousePos.current.y;

    setModalSize((prev) => {
      const newWidth =
        isResizing.type !== "vertical"
          ? Math.min(
              maxSizeRef.current.width,
              Math.max(400, prev.width + deltaX * 2)
            )
          : prev.width;

      const newHeight =
        isResizing.type !== "horizontal"
          ? Math.min(
              maxSizeRef.current.height,
              Math.max(300, prev.height + deltaY * 2)
            )
          : prev.height;

      return {
        width: newWidth,
        height: newHeight,
      };
    });

    initialMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    setIsResizing({ active: false, type: null });
  };

  useEffect(() => {
    const handleDocumentMouseMove = (e) => {
      if (isResizing.active) {
        handleMouseMove(e);
      }
    };

    const handleDocumentMouseUp = () => {
      if (isResizing.active) {
        handleMouseUp();
      }
    };

    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
    };
  }, [isResizing.active]);

  const ResizeHandle = ({ type, position }) => (
    <IconButton
      sx={{
        position: "absolute",
        ...position,
        backgroundColor: colors.grey_8percent,
        "&:hover": {
          backgroundColor: colors.divider2,
        },
        cursor:
          type === "horizontal"
            ? "ew-resize"
            : type === "vertical"
            ? "ns-resize"
            : "nwse-resize",
        padding: "4px",
        minWidth: "28px",
        minHeight: "28px",
      }}
      onMouseDown={(e) => handleMouseDown(e, type)}
    >
      {type === "horizontal" ? (
        <MoreVertIcon fontSize="small" />
      ) : type === "vertical" ? (
        <DragHandleIcon fontSize="small" />
      ) : (
        <OpenInFullIcon
          fontSize="small"
          sx={{
            transform: "rotate(90deg)",
          }}
        />
      )}
    </IconButton>
  );

  return (
    <Modal
      open={show}
      onClose={handleClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: modalSize.width,
          height: modalSize.height,
          bgcolor: colors.common_white,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          transition: isResizing.active ? "none" : "width 0.1s, height 0.1s",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: "relative",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Evidence evidence={evidence} />
        </Box>{" "}
        {/* Resize handles */}
        <ResizeHandle
          type="horizontal"
          position={{
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
        <ResizeHandle
          type="vertical"
          position={{
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
        <ResizeHandle
          type="both"
          position={{
            right: 4,
            bottom: 4,
          }}
        />
      </Box>
    </Modal>
  );
};

export default EvidenceModal;
